import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { productService } from "../../services/product.service";
import { LoadingButton } from "../common/LoadingButton";

// ─── Schema ──────────────────────────────────────────────────────────────────

const productSchema = z.object({
  // Required fields
  name: z.string().min(3, "Name must be at least 3 characters").max(200),
  // Auto-generated fields (optional on form but required on submit)
  sku: z
    .string()
    .min(2, "SKU must be at least 2 characters")
    .max(100)
    .optional(),
  barcode: z.string().optional(),
  // Classification
  category: z.string().min(1, "Category is required"),
  subcategory: z.string().optional(),
  manufacturer: z.string().optional(),
  // Pricing (required)
  purchasePrice: z.number().min(0, "Must be positive"),
  sellingPrice: z.number().min(0, "Must be positive"),
  mrp: z.number().min(0, "Must be positive"),
  gstPercent: z.number().min(0).max(100).optional(),
  isGstExempted: z.boolean().optional(),
  // Drug information (optional - show in expandable section)
  saltComposition: z.string().optional(),
  composition: z.string().optional(),
  scheduleType: z
    .enum(["GENERAL", "SCHEDULE_G", "SCHEDULE_H", "SCHEDULE_H1", "SCHEDULE_X"])
    .optional(),
  // Additional (optional - in collapsible)
  unit: z.string().optional(),
  hsnCode: z.string().optional(),
  minStockLevel: z.number().min(0).optional(),
  maxStockLevel: z.number().min(0).optional(),
  description: z.string().optional(),
  sideEffects: z.string().optional(),
  strength: z.string().optional(),
  status: z.enum(["active", "inactive"]).optional(),
});

export type ProductFormData = z.infer<typeof productSchema>;

// ─── Unit options ────────────────────────────────────────────────────────────

const UNIT_OPTIONS = [
  { value: "strip", label: "Strip" },
  { value: "bottle", label: "Bottle" },
  { value: "box", label: "Box" },
  { value: "tube", label: "Tube" },
  { value: "vial", label: "Vial" },
  { value: "pack", label: "Pack" },
  { value: "tablet", label: "Tablet" },
  { value: "capsule", label: "Capsule" },
  { value: "ml", label: "ML" },
  { value: "gm", label: "GM" },
  { value: "piece", label: "Piece" },
] as const;

const GST_OPTIONS = [0, 5, 12, 18, 28];

// ─── Props ───────────────────────────────────────────────────────────────────

interface ProductFormProps {
  mode: "create" | "edit";
  defaultValues?: Partial<ProductFormData>;
  onSubmit: (data: ProductFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ProductForm({
  mode,
  defaultValues,
  onSubmit,
  onCancel,
  isLoading = false,
}: ProductFormProps) {
  const [categorySuggestions, setCategorySuggestions] = useState<string[]>([]);
  const [subcategorySuggestions, setSubcategorySuggestions] = useState<
    string[]
  >([]);
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [showSubcategorySuggestions, setShowSubcategorySuggestions] =
    useState(false);
  const [showDrugInfo, setShowDrugInfo] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      gstPercent: 5,
      isGstExempted: false,
      scheduleType: "GENERAL",
      status: "active",
      category: "",
      unit: "strip",
      minStockLevel: 10,
      ...defaultValues,
    },
  });

  const categoryValue = watch("category");
  const purchasePrice = watch("purchasePrice") || 0;
  const sellingPrice = watch("sellingPrice") || 0;
  const mrp = watch("mrp") || 0;
  const isGstExempted = watch("isGstExempted") || false;

  // Auto-expand drug info section if salt composition or composition exists
  useEffect(() => {
    if (
      defaultValues?.saltComposition ||
      defaultValues?.composition ||
      defaultValues?.sideEffects
    ) {
      setShowDrugInfo(true);
    }
    if (
      defaultValues?.hsnCode ||
      defaultValues?.maxStockLevel ||
      defaultValues?.strength
    ) {
      setShowAdvanced(true);
    }
  }, [defaultValues]);

  useEffect(() => {
    if (isGstExempted) {
      setValue("gstPercent", 0);
    }
  }, [isGstExempted, setValue]);

  // Margins
  const marginOnSelling =
    purchasePrice > 0 && sellingPrice > 0
      ? (((sellingPrice - purchasePrice) / sellingPrice) * 100).toFixed(1)
      : "0";
  const marginOnMRP =
    purchasePrice > 0 && mrp > 0
      ? (((mrp - purchasePrice) / mrp) * 100).toFixed(1)
      : "0";

  // ─── Category Suggestions ─────────────────────────────────────────────────

  const loadCategorySuggestions = useCallback(async (search?: string) => {
    try {
      const data = await productService.getCategorySuggestions(search);
      setCategorySuggestions(data.categories || []);
    } catch {
      // ignore
    }
  }, []);

  const loadSubcategorySuggestions = useCallback(
    async (category?: string, search?: string) => {
      try {
        const data = await productService.getSubcategorySuggestions(
          category,
          search,
        );
        setSubcategorySuggestions(data.subcategories || []);
      } catch {
        // ignore
      }
    },
    [],
  );

  useEffect(() => {
    loadCategorySuggestions();
  }, [loadCategorySuggestions]);

  useEffect(() => {
    if (categoryValue) {
      loadSubcategorySuggestions(categoryValue);
    }
  }, [categoryValue, loadSubcategorySuggestions]);

  // Auto-suggest HSN code when category changes (create mode only)
  const autoSuggestHsn = async (category: string) => {
    if (mode !== "create") return;
    try {
      const hsn = await productService.suggestHsnCode(category);
      if (hsn) {
        setValue("hsnCode", hsn.hsnCode);
        setValue("gstPercent", hsn.gstRate);
      }
    } catch {
      // ignore
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* ── Section 1: Essential Info ──────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-1 text-gray-800">
          {mode === "create" ? "Product Details" : "Basic Information"}
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          Fields marked with * are required
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Product Name - Full Width */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register("name")}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="e.g., Paracetamol 500mg Tablet"
              autoFocus={mode === "create"}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                {...register("category")}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="e.g., Allopathy, Ayurvedic, OTC"
                onFocus={() => setShowCategorySuggestions(true)}
                onBlur={() =>
                  setTimeout(() => setShowCategorySuggestions(false), 200)
                }
                onChange={(e) => {
                  setValue("category", e.target.value);
                  loadCategorySuggestions(e.target.value);
                  autoSuggestHsn(e.target.value);
                }}
              />
              {showCategorySuggestions && categorySuggestions.length > 0 && (
                <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto mt-1">
                  {categorySuggestions
                    .filter((cat) =>
                      cat
                        .toLowerCase()
                        .includes((categoryValue || "").toLowerCase()),
                    )
                    .slice(0, 8)
                    .map((cat) => (
                      <li
                        key={cat}
                        className="px-3 py-2 hover:bg-teal-50 cursor-pointer text-sm"
                        onMouseDown={() => {
                          setValue("category", cat);
                          setShowCategorySuggestions(false);
                          autoSuggestHsn(cat);
                        }}
                      >
                        {cat}
                      </li>
                    ))}
                </ul>
              )}
            </div>
            {errors.category && (
              <p className="mt-1 text-sm text-red-600">
                {errors.category.message}
              </p>
            )}
          </div>

          {/* Subcategory */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subcategory
            </label>
            <div className="relative">
              <input
                {...register("subcategory")}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="e.g., Tablets, Syrups, Drops"
                onFocus={() => setShowSubcategorySuggestions(true)}
                onBlur={() =>
                  setTimeout(() => setShowSubcategorySuggestions(false), 200)
                }
                onChange={(e) => {
                  setValue("subcategory", e.target.value);
                  loadSubcategorySuggestions(categoryValue, e.target.value);
                }}
              />
              {showSubcategorySuggestions &&
                subcategorySuggestions.length > 0 && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-auto mt-1">
                    {subcategorySuggestions.slice(0, 8).map((sub) => (
                      <li
                        key={sub}
                        className="px-3 py-2 hover:bg-teal-50 cursor-pointer text-sm"
                        onMouseDown={() => {
                          setValue("subcategory", sub);
                          setShowSubcategorySuggestions(false);
                        }}
                      >
                        {sub}
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          </div>

          {/* Manufacturer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Manufacturer
            </label>
            <input
              {...register("manufacturer")}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="e.g., Sun Pharma, Cipla"
            />
          </div>

          {/* Unit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unit / Pack Type
            </label>
            <select
              {...register("unit")}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {UNIT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* SKU (auto-generated, shown in edit) */}
          {mode === "edit" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SKU{" "}
                <span className="text-xs text-gray-400">(auto-generated)</span>
              </label>
              <input
                {...register("sku")}
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
                readOnly
              />
            </div>
          )}

          {/* Barcode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Barcode <span className="text-xs text-gray-400">(optional)</span>
            </label>
            <input
              {...register("barcode")}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Scan or leave blank to auto-generate"
            />
          </div>
        </div>
      </div>

      {/* ── Section 2: Pricing ─────────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4 text-gray-800">Pricing</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Price <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">₹</span>
              <input
                {...register("purchasePrice", { valueAsNumber: true })}
                type="number"
                step="0.01"
                min="0"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="0.00"
              />
            </div>
            {errors.purchasePrice && (
              <p className="mt-1 text-sm text-red-600">
                {errors.purchasePrice.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Selling Price <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">₹</span>
              <input
                {...register("sellingPrice", { valueAsNumber: true })}
                type="number"
                step="0.01"
                min="0"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="0.00"
              />
            </div>
            {errors.sellingPrice && (
              <p className="mt-1 text-sm text-red-600">
                {errors.sellingPrice.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              MRP <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">₹</span>
              <input
                {...register("mrp", { valueAsNumber: true })}
                type="number"
                step="0.01"
                min="0"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder="0.00"
              />
            </div>
            {errors.mrp && (
              <p className="mt-1 text-sm text-red-600">{errors.mrp.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              GST %
            </label>
            <label className="mb-2 flex items-center gap-2 text-xs font-medium text-gray-600">
              <input
                {...register("isGstExempted")}
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
              />
              Mark as GST exempt
            </label>
            <select
              {...register("gstPercent", { valueAsNumber: true })}
              disabled={isGstExempted}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${isGstExempted ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
            >
              {GST_OPTIONS.map((rate) => (
                <option key={rate} value={rate}>
                  {rate}%
                </option>
              ))}
            </select>
            {isGstExempted && (
              <p className="mt-1 text-xs text-gray-500">
                Exempt products are billed with 0% GST.
              </p>
            )}
          </div>
        </div>

        {/* Margin display */}
        <div className="mt-4 flex gap-4">
          <div className="flex-1 px-3 py-2 bg-green-50 border border-green-200 rounded-md text-center">
            <span className="text-xs text-gray-600">Margin (Selling)</span>
            <div className="font-semibold text-green-700">
              {marginOnSelling}%
            </div>
          </div>
          <div className="flex-1 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-center">
            <span className="text-xs text-gray-600">Margin (MRP)</span>
            <div className="font-semibold text-blue-700">{marginOnMRP}%</div>
          </div>
        </div>
      </div>

      {/* ── Section 3: Drug / Medicine Info (Collapsible) ─────────────── */}
      <div className="bg-white rounded-lg shadow">
        <button
          type="button"
          onClick={() => setShowDrugInfo(!showDrugInfo)}
          className="w-full p-6 text-left flex justify-between items-center hover:bg-gray-50 rounded-lg"
        >
          <div>
            <span className="font-semibold text-gray-800">
              Medicine / Drug Info
            </span>
            <span className="text-sm text-gray-500 ml-2">
              Salt composition, schedule type, side effects
            </span>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${showDrugInfo ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {showDrugInfo && (
          <div className="px-6 pb-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Salt Composition */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Salt Composition
                </label>
                <input
                  {...register("saltComposition")}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Paracetamol (500mg) + Caffeine (65mg)"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Used for finding alternate medicines. Separate salts with + or
                  comma.
                </p>
              </div>

              {/* Composition */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Composition / Generic Name
                </label>
                <input
                  {...register("composition")}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Paracetamol 500mg"
                />
              </div>

              {/* Schedule Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule Type
                </label>
                <select
                  {...register("scheduleType")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="GENERAL">General (OTC)</option>
                  <option value="SCHEDULE_G">Schedule G</option>
                  <option value="SCHEDULE_H">Schedule H (Rx only)</option>
                  <option value="SCHEDULE_H1">Schedule H1</option>
                  <option value="SCHEDULE_X">Schedule X (Controlled)</option>
                </select>
              </div>

              {/* Strength */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Strength
                </label>
                <input
                  {...register("strength")}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., 500mg, 10ml"
                />
              </div>

              {/* Side Effects */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Side Effects
                </label>
                <textarea
                  {...register("sideEffects")}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Known side effects..."
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Section 4: Advanced Settings (Collapsible) ────────────────── */}
      <div className="bg-white rounded-lg shadow">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full p-6 text-left flex justify-between items-center hover:bg-gray-50 rounded-lg"
        >
          <div>
            <span className="font-semibold text-gray-800">
              Advanced Settings
            </span>
            <span className="text-sm text-gray-500 ml-2">
              HSN code, stock levels, description
            </span>
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${showAdvanced ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {showAdvanced && (
          <div className="px-6 pb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  HSN Code
                </label>
                <input
                  {...register("hsnCode")}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., 30049011"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Stock (Reorder Level)
                </label>
                <input
                  {...register("minStockLevel", { valueAsNumber: true })}
                  type="number"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Stock Level
                </label>
                <input
                  {...register("maxStockLevel", { valueAsNumber: true })}
                  type="number"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  {...register("status")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  {...register("description")}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Product description (optional)"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Actions ────────────────────────────────────────────────────── */}
      <div className="flex justify-between items-center">
        {mode === "create" && (
          <p className="text-sm text-gray-500">
            SKU & barcode will be auto-generated. You can add pack sizes after
            creating.
          </p>
        )}
        {mode === "edit" && <div />}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <LoadingButton
            type="submit"
            isLoading={isLoading}
            loadingText={mode === "create" ? "Creating..." : "Updating..."}
          >
            {mode === "create" ? "Create Product" : "Update Product"}
          </LoadingButton>
        </div>
      </div>
    </form>
  );
}
